/**
 * Design tokens for consistent styling across the app
 */

export const borders = {
  // Border colors
  section: 'border-divider', // Main section borders (custom neutral gray from Figma)
  medium: 'border-gray-700', // Medium borders where needed
  light: 'border-gray-600', // Lighter borders where needed
  accent: 'border-[#828699]', // Accent borders (existing button style)

  // Border utilities
  bottom: 'border-b',
  right: 'border-r',
  all: 'border',
} as const;

export const colors = {
  // Text colors
  primary: 'text-white',
  secondary: 'text-[#808080]',
  disabled: 'text-[#808080]/60',
  success: 'text-brand-green',
  danger: 'text-brand-red',
  warning: 'text-[#ffd166]',

  // Price direction colors
  priceUp: 'text-price-up',
  priceDown: 'text-price-down',

  // Background colors
  surface: 'bg-[#292929]', // Primary surface color
  surfaceHover: 'bg-[#292929]/80', // Hovered surface
  sectionBg: 'bg-white/10',
  hoverBg: 'bg-white/15',
  inputBg: 'bg-[#292929]', // Input background
  inputDisabled: 'bg-[#292929]/50', // Disabled input

  // Border colors
  border: 'border-[#808080]/60', // Default border
  borderHover: 'border-[#808080]', // Focused/hovered border
  borderSubtle: 'border-[#808080]/30', // Very subtle borders
  divider: 'border-[#151515]', // Section dividers
} as const;

export const buttons = {
  // Button backgrounds
  primary: 'bg-brand-green text-brand-dark',
  secondary: 'bg-[#292929] text-white',
  disabled: 'bg-[#292929]/50 text-[#808080]',

  // Button states
  primaryHover: 'hover:bg-brand-green/90',
  secondaryHover: 'hover:bg-[#292929]/80',

  // Button borders
  secondaryBorder: 'border border-[#808080]/60',
  focusBorder: 'focus:border-[#808080]',
} as const;

export const spacing = {
  // Section padding
  sectionPx: 'px-3',
  sectionPy: 'py-2',

  // Container spacing
  containerPx: 'px-2',
  containerPy: 'py-0.5',
} as const;

export const layout = {
  // Flex utilities
  flex1: 'flex-1',
  flex0: 'flex-0',
  flexRow: 'flex',
  flexCol: 'flex flex-col',

  // Common layout patterns
  sectionRow: `flex ${borders.bottom} ${borders.section}`,
  sectionCell: `${spacing.sectionPx} ${spacing.sectionPy} flex items-center`,
} as const;

/**
 * Utility function to combine border classes
 */
export function sectionBorder(sides: ('bottom' | 'right')[] = []) {
  const classes: string[] = [borders.section];
  if (sides.includes('bottom')) {
    classes.push(borders.bottom);
  }
  if (sides.includes('right')) {
    classes.push(borders.right);
  }
  return classes.join(' ');
}

/**
 * Common section styling patterns
 */
export const sections = {
  // Header-style sections
  headerRow: `flex ${sectionBorder(['bottom'])}`,
  headerCell: `${layout.flex1} ${spacing.sectionPx} ${spacing.sectionPy} flex items-center`,
  headerCellWithBorder: `${layout.flex1} ${sectionBorder(['right'])} ${spacing.sectionPx} ${spacing.sectionPy} flex items-center`,

  // Content sections
  contentRow: `flex ${sectionBorder(['bottom'])}`,
  contentCell: `${layout.flex1} ${spacing.sectionPx} ${spacing.sectionPy} flex flex-col`,
  contentCellWithBorder: `${layout.flex1} ${sectionBorder(['right'])} ${spacing.sectionPx} ${spacing.sectionPy} flex flex-col`,
} as const;

/**
 * Common component styles
 */
export const components = {
  // Input styles
  input: `${colors.inputBg} ${colors.primary} ${colors.border} focus:${colors.borderHover} focus:outline-none`,
  inputDisabled: `${colors.inputDisabled} ${colors.disabled} ${colors.borderSubtle} cursor-not-allowed`,

  // Button styles
  button: `${buttons.secondary} ${buttons.secondaryBorder} ${buttons.secondaryHover} ${buttons.focusBorder}`,
  buttonPrimary: `${buttons.primary} ${buttons.primaryHover}`,
  buttonDisabled: `${buttons.disabled} cursor-not-allowed`,

  // Panel styles
  panel: `bg-brand-dark/95 backdrop-blur-sm ${colors.divider} border-t-2`,
  modal: `${colors.surface} ${colors.border}`,

  // Text styles
  label: colors.secondary,
  labelSmall: `${colors.secondary} text-xs`,
  value: colors.primary,
} as const;
